import { useReactiveVar } from '@apollo/client'
import { isEmbededErrorVar, isImageErrorVar } from 'modulesCommon/store'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useOnScreen } from 'shared/hooks'

import { TRouterParams } from 'shared/types'
import { matchImgTag, preloadImagesList } from 'shared/utils/preloadImages'
import { scrollToTop } from 'shared/utils'
import { matchEmbededTag, removeTikTagsEmbeded } from '../utils'
import { useShareStory } from 'modules/Feed/graphql/mutations/shareStory'
import { Tag } from 'shared/__gql-types'

const useStory = ({ story, removeFeed, updateFeed, storyRef }: any) => {
  const { id } = useParams<TRouterParams>()

  const isImgError = useReactiveVar(isImageErrorVar)
  const isEmbededError = useReactiveVar(isEmbededErrorVar)

  const [isEdit, toggleEdit] = useState(false)
  const [isErrorOnEdit, setError] = useState(false)
  const [isCollapsed, toggleCollapsed] = useState(false)
  const [isBigCard, toggleBigCard] = useState(false)
  const [preloaded, setPreloaded] = useState(true)
  const [hasTiktok, setContainsTiktok] = useState(false)
  const [isTiktokOpened, setContainsTiktokOpened] = useState(false)
  const [isTouched, setTouched] = useState(false)

  const isStoryVisible = useOnScreen(storyRef)

  const preloadArticle = useCallback(() => {
    const imgsTags = matchImgTag(story.body)
    if (imgsTags) {
      setPreloaded(false)

      preloadImagesList(imgsTags).finally(() => {
        setPreloaded(true)
      })
    }
  }, [story.body])

  const catchTiktokContent = useCallback(
    (text: string) => matchEmbededTag(text) && text.includes('tiktok-embed') && !text.includes('data-embeded-response'),
    []
  )

  const catchTikTokArticle = useCallback(() => {
    const tikTokTags = catchTiktokContent(story.body)
    if (tikTokTags) {
      setContainsTiktok(true)
    }
  }, [story.body, catchTiktokContent])

  const article = useMemo(
    () => (isCollapsed && hasTiktok && !isTiktokOpened ? removeTikTagsEmbeded(story.body) : story.body),
    [story.body, isCollapsed, hasTiktok, isTiktokOpened]
  )

  const handleToggleCollapsed = useCallback(
    (value: boolean) => {
      toggleCollapsed(value)
      if (!value) {
        setContainsTiktokOpened(true)
      }
    },
    [toggleCollapsed]
  )

  const toggleEditHandler = useCallback(() => {
    toggleEdit(!isEdit)
    if (isEdit) {
      setContainsTiktokOpened(true)
    }
  }, [isEdit])

  const removeFeedHandler = useCallback(
    () => (removeFeed ? removeFeed({ variables: { id: story.id } }) : null),
    [story.id, removeFeed]
  )

  const alertMessage = useMemo(() => {
    if (isEmbededError) return 'Something went wrong'
    return isImgError ? 'Maximum image size 3Mb' : 'Error on edit story'
  }, [isImgError, isEmbededError])

  const { mutate: shareStory } = useShareStory()

  const shareStoryHandler = useCallback(
    async (data: { argument_id: number; message: string }, cb: () => void) => {
      return shareStory({
        variables: {
          input: data
        }
      }).then(({ data: { createGlobalInboxItem } }: any) => {
        if (!createGlobalInboxItem.id) {
          setError(true)
        } else {
          cb()
        }
      })
    },
    [shareStory]
  )

  const updateFeedHandler = useCallback(
    (args) => {
      const { __typename, ...input } = args
      return updateFeed({
        variables: {
          input: {
            id: story.id,
            campaign_id: story.campaign_id,
            ...input,
            tags: args.tags?.map(({ __typename: string, ...rest }: Tag) => rest) ?? [],
          }
        }
      }).then(({ data: { updateFeed } }: any) => {
        if (!updateFeed.id) {
          setError(true)
        } else {
          scrollToTop()
        }
      })
    },
    [story, updateFeed]
  )

  useEffect(() => {
    if (isStoryVisible) preloadArticle()
  }, [preloadArticle, isStoryVisible])

  useEffect(() => {
    if (isStoryVisible) catchTikTokArticle()
  }, [catchTikTokArticle, isStoryVisible])

  useEffect(() => {
    if (isErrorOnEdit) {
      const notificationTimer = setTimeout(() => {
        setError(false)
      }, 2000)

      return () => {
        clearTimeout(notificationTimer)
      }
    }
  }, [isErrorOnEdit])

  useEffect(() => {
    if (isTouched && isCollapsed) {
      storyRef?.current?.scrollIntoView({
        block: 'center',
        behavior: 'smooth'
      })
    }
  }, [isCollapsed, isTouched, storyRef])

  return {
    updateFeedHandler,
    toggleEditHandler,
    article,
    id: (id as string),
    isBigCard,
    toggleBigCard,
    preloaded,
    setTouched,
    handleToggleCollapsed,
    removeFeedHandler,
    alertMessage,
    storyRef,
    isCollapsed,
    hasTiktok,
    isTiktokOpened,
    shareStoryHandler,
    setPreloaded
  }
}

export default useStory
